import { useMemo, useRef, useImperativeHandle, forwardRef, useState } from 'react';
import { format, parseISO } from 'date-fns';
import 'react-virtualized/styles.css';
import { AutoSizer, List, ListRowProps } from '../../atoms';
import { classNames } from 'primereact/utils';

const HIDDEN_PAGE_SCROLL_DELAY_MS = 10;

interface TimelineMonth {
  id: string;
  month: string;
  date: string;
}

interface TimelineYear {
  year: number;
  months: TimelineMonth[];
}

type FlatTimelineItem = { type: 'year'; year: number } | { type: 'month'; year: number; month: string; id: string };

interface ReactVirtualizedTimelineProps {
  dates: string[];
  onClick?: (id: string) => void;
  className?: string;
}

export interface ReactVirtualizedTimelineRef {
  scrollToRowById: (id: string) => void;
  scrollToRowByDate: (dateStr: string) => void;
}

export const ReactVirtualizedTimeline = forwardRef<ReactVirtualizedTimelineRef, ReactVirtualizedTimelineProps>(
  ({ dates, onClick, className }, ref) => {
    const listRef = useRef<List>(null);
    const [isHiddenScrolling, setIsHiddenScrolling] = useState<boolean>(false);
    const [activeId, setActiveId] = useState<string | null>(null);

    const timelineStructure: TimelineYear[] = useMemo(() => {
      const map = new Map<number, Map<string, TimelineMonth>>();

      for (const dateStr of dates) {
        const date = parseISO(dateStr);
        const year = date.getFullYear();
        const month = format(date, 'MMM');

        if (!map.has(year)) {
          map.set(year, new Map());
        }

        const yearMap = map.get(year)!;
        if (!yearMap.has(month)) {
          yearMap.set(month, {
            id: `${year}-${month}`,
            month,
            date: dateStr,
          });
        }
      }

      return Array.from(map.entries())
        .sort(([a], [b]) => a - b)
        .map(([year, monthMap]) => ({
          year,
          months: Array.from(monthMap.values()).sort((a, b) => (parseISO(a.date) > parseISO(b.date) ? 1 : -1)),
        }));
    }, [dates]);

    const flatList: FlatTimelineItem[] = useMemo(() => {
      const result: FlatTimelineItem[] = [];

      for (const yearBlock of timelineStructure) {
        if (yearBlock.months.length === 0) continue;

        result.push({ type: 'year', year: yearBlock.year });
        for (const month of yearBlock.months) {
          result.push({
            type: 'month',
            year: yearBlock.year,
            month: month.month,
            id: month.id,
          });
        }
      }

      return result;
    }, [timelineStructure]);

    useImperativeHandle(ref, () => ({
      scrollToRowById: (id: string) => {
        const index = flatList.findIndex((item) => item.type === 'month' && item.id === id);
        if (index !== -1 && activeId !== id) {
          setIsHiddenScrolling(true);

          setTimeout(() => {
            listRef.current?.scrollToRow(index);
            setActiveId(id);
            setIsHiddenScrolling(false);
          }, HIDDEN_PAGE_SCROLL_DELAY_MS);
        }
      },
      scrollToRowByDate: (dateStr: string) => {
        const date = parseISO(dateStr);
        const year = date.getFullYear();
        const month = format(date, 'MMM');
        const id = `${year}-${month}`;
        const index = flatList.findIndex((item) => item.type === 'month' && item.id === id);

        if (index !== -1 && activeId !== id) {
          setIsHiddenScrolling(true);

          setTimeout(() => {
            listRef.current?.scrollToRow(index);
            setActiveId(id);
            setIsHiddenScrolling(false);
          }, HIDDEN_PAGE_SCROLL_DELAY_MS);
        }
      },
    }));

    const rowRenderer = ({ index, key, style }: ListRowProps) => {
      const item = flatList[index];
      const isYear = item.type === 'year';
      const isActive = item.type === 'month' && item.id === activeId;
      const isFirst = index === 0;
      const isLast = index === flatList.length - 1;

      return (
        <div
          data-testid={isYear ? item.year : item.id}
          key={key}
          style={style}
          className="relative flex cursor-pointer items-center justify-start"
          onClick={() => {
            if (isYear) {
              const yearBlock = timelineStructure.find((y) => y.year === item.year);
              const firstMonth = yearBlock?.months[0];
              if (firstMonth) {
                setActiveId(firstMonth.id);
                onClick?.(firstMonth.id);
              }
            } else {
              setActiveId(item.id);
              onClick?.(item.id);
            }
          }}
        >
          {/* Connecting line */}
          <div
            className={classNames(
              'bg-fletchers-green-500 absolute top-0 left-5 z-0 h-full w-0.25 -translate-x-1/2 transform',
            )}
            style={{
              top: isFirst ? '50%' : 0,
              bottom: isLast ? '50%' : 0,
              height: isFirst || isLast ? '50%' : '100%',
            }}
          />
          {/* Circle */}
          <div className="z-10 flex w-10 flex-col items-center justify-center">
            <div
              className={classNames('border-fletchers-green-500 bg-fletchers-white h-2 w-2 rounded-full border-1', {
                'h-3 w-3 border-3': isYear,
                '!bg-fletchers-red-500 !border-fletchers-red-500': isActive,
              })}
            />
          </div>
          {/* Label */}
          <div
            className={classNames(
              'text-fletchers-green-500',
              isYear ? 'text-[0.8125rem]/4 font-[700]' : 'text-[0.75rem]/4 font-[400]',
              {
                '!text-fletchers-red-500': isActive,
              },
            )}
          >
            {isYear ? item.year : item.month}
          </div>
        </div>
      );
    };

    return (
      <div className={classNames('relative h-full w-full', className)}>
        <AutoSizer>
          {({ height, width }) => (
            <List
              ref={listRef}
              width={width}
              height={height}
              rowCount={flatList.length}
              rowHeight={32}
              rowRenderer={rowRenderer}
              scrollToAlignment={isHiddenScrolling ? 'center' : 'auto'}
            />
          )}
        </AutoSizer>
      </div>
    );
  },
);
